/**
 * DlqRedriveModal
 *
 * Confirmation modal for redriving a dead-lettered message back to its live
 * queue. Shown when an admin clicks "Redrive" on a DLQ row in QueueHealthPage.
 *
 * US-24-GR-05 — DLQ Redrive Button
 */

import { useEffect, useRef, useState } from 'react';

export interface DlqRedriveModalProps {
  isOpen: boolean;
  queue: 'agent-tasks' | 'agent-events';
  messageId: string;
  deadLetterReason: string;
  payloadPreview: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/** Minimal spinner — animation gated on motion-safe (prefers-reduced-motion) */
function Spinner() {
  return (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full motion-safe:animate-spin"
    />
  );
}

export function DlqRedriveModal({
  isOpen,
  queue,
  messageId,
  deadLetterReason,
  payloadPreview,
  onConfirm,
  onCancel,
}: DlqRedriveModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [isInFlight, setIsInFlight] = useState(false);

  // Focus the Redrive button when modal opens; restore focus on close.
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      const id = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    } else {
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen]);

  // ESC to cancel + focus trap
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!isInFlight) onCancel();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isInFlight, onCancel]);

  if (!isOpen) return null;

  async function handleConfirm() {
    if (isInFlight) return;
    setIsInFlight(true);
    try {
      await onConfirm();
    } finally {
      setIsInFlight(false);
    }
  }

  const preview = payloadPreview.slice(0, 500);
  const isTruncated = payloadPreview.length > 500;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        aria-hidden="true"
        onClick={() => { if (!isInFlight) onCancel(); }}
      />

      {/* Dialog */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dlq-redrive-title"
          aria-describedby="dlq-redrive-desc"
          className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
        >
          {/* Title */}
          <h2
            id="dlq-redrive-title"
            className="text-sm font-semibold text-zinc-100"
          >
            Redrive dead-lettered message?
          </h2>

          <div id="dlq-redrive-desc" className="flex flex-col gap-3">
            {/* Dead-letter reason */}
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1">Dead-letter reason</p>
              <div className="rounded-lg bg-red-950/40 border border-red-500/30 px-3 py-2">
                <p className="text-xs text-red-300 break-words">
                  {deadLetterReason || '(no reason recorded)'}
                </p>
              </div>
            </div>

            {/* Queue + messageId meta */}
            <div className="flex gap-2 flex-wrap">
              <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-400">
                queue: <span className="text-zinc-200">{queue}</span>
              </span>
              <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-400 font-mono truncate max-w-[240px]">
                {messageId}
              </span>
            </div>

            {/* Payload preview */}
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1">
                Payload preview{isTruncated ? ' (truncated to 500 chars)' : ''}
              </p>
              <pre className="text-xs text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-y-auto whitespace-pre-wrap break-words font-mono max-h-40">
                {preview}
              </pre>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={isInFlight}
              aria-label="Cancel — do not redrive this message"
              className="min-h-[48px] px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-sm font-medium hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>

            <button
              ref={confirmButtonRef}
              type="button"
              onClick={() => void handleConfirm()}
              disabled={isInFlight}
              aria-label={`Redrive message ${messageId}`}
              className="min-h-[48px] px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-zinc-950 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              {isInFlight ? (
                <>
                  <Spinner />
                  Redriving…
                </>
              ) : (
                'Redrive'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
